import * as Sentry from '@sentry/react'

export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN
  const environment = import.meta.env.VITE_SENTRY_ENVIRONMENT || 'development'
  const enabled = import.meta.env.VITE_SENTRY_ENABLED === 'true'

  if (!dsn) {
    console.warn('Sentry DSN not configured')
    return
  }

  if (!enabled) {
    console.info('Sentry disabled')
    return
  }

  Sentry.init({
    dsn,
    environment,
    integrations: [Sentry.browserTracingIntegration()],
    // Performance Monitoring
    tracesSampleRate: environment === 'production' ? 0.1 : 1.0,
    // Release tracking
    release: `compass-fe@${import.meta.env.VITE_APP_VERSION || '0.0.0'}`,
    // Error filtering
    beforeSend(event, hint) {
      // Filter out certain errors if needed
      if (event.exception) {
        const error = hint.originalException
        // Example: Don't send network errors in development
        if (environment === 'development' && error instanceof Error) {
          if (error.message.includes('Network Error')) {
            return null
          }
        }
      }
      return event
    },
  })
}

export function setSentryUser(user: {
  id: string
  email?: string
  username?: string
}) {
  Sentry.setUser({
    id: user.id,
    email: user.email,
    username: user.username,
  })
}

export function clearSentryUser() {
  Sentry.setUser(null)
}

export function captureApiError(
  error: unknown,
  context: {
    endpoint: string
    method: string
    statusCode?: number
    requestData?: unknown
  }
) {
  Sentry.withScope((scope) => {
    scope.setContext('api_request', {
      endpoint: context.endpoint,
      method: context.method,
      statusCode: context.statusCode,
    })

    scope.setTag('error_type', 'api_error')
    scope.setTag('http_status', context.statusCode || 'unknown')
    scope.setTag('http_method', context.method)

    if (context.statusCode) {
      if (context.statusCode >= 500) {
        scope.setLevel('error')
      } else if (context.statusCode >= 400) {
        scope.setLevel('warning')
      }
    }

    if (context.requestData) {
      scope.setExtra('request_data', redactForSentry(context.requestData))
    }

    if (error instanceof Error) {
      Sentry.captureException(error)
    } else {
      Sentry.captureMessage(`API Error: ${context.endpoint}`, 'error')
    }
  })
}

const SENSITIVE_KEY_PATTERN =
  /^(token|password|passwd|secret|authorization|auth|cookie|api[_-]?key|client[_-]?secret|access[_-]?token|refresh[_-]?token|id[_-]?token|session[_-]?id|jwt)$/i

const MAX_STRING_LENGTH = 1000
const MAX_DEPTH = 6
const REDACTED = '[REDACTED]'

/**
 * Strip sensitive fields and truncate large payloads before sending to Sentry.
 * Recursive but depth-capped; loses information rather than risking PII/secret leakage.
 */
export function redactForSentry(value: unknown, depth = 0): unknown {
  if (depth > MAX_DEPTH) {
    return '[Truncated: max depth]'
  }

  if (value === null || value === undefined) {
    return value
  }

  if (typeof value === 'string') {
    return value.length > MAX_STRING_LENGTH
      ? `${value.slice(0, MAX_STRING_LENGTH)}…[truncated ${value.length - MAX_STRING_LENGTH} chars]`
      : value
  }

  if (typeof value !== 'object') {
    return value
  }

  if (Array.isArray(value)) {
    return value.slice(0, 50).map((item) => redactForSentry(item, depth + 1))
  }

  const out: Record<string, unknown> = {}
  for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
    out[key] = SENSITIVE_KEY_PATTERN.test(key)
      ? REDACTED
      : redactForSentry(val, depth + 1)
  }
  return out
}

export function addSentryBreadcrumb(
  message: string,
  category: string,
  data?: Record<string, unknown>
) {
  Sentry.addBreadcrumb({
    message,
    category,
    level: 'info',
    data,
  })
}

export function safeParseRequestData(data: unknown): unknown {
  if (!data) {
    return undefined
  }

  if (typeof data === 'object') {
    return data
  }

  if (typeof data === 'string') {
    try {
      return JSON.parse(data)
    } catch {
      return data
    }
  }

  return data
}
